import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ForgotPassword.css';
import maskGroupImage from '../../../assets/Mask group.png';
import logoWhite from '../../../assets/logo-white.png';
import ApiService from '../../services/ApiService';

// Healthcare Logo Component
const HealthcareLogo: React.FC = () => {
  return (
    <div className="healthcare-logo">
      <div className="logo-icon">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          {/* Heart outline */}
          <path
            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
          {/* Plus sign inside heart */}
          <path
            d="M12 8v8M8 12h8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <span className="logo-text">Healthcare</span>
    </div>
  );
};

const ForgotPassword: React.FC = () => {
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsLoading(true);

    try {
      const apiService = ApiService.getInstance();
      const baseUrl = apiService.getAuthServiceUrl();

      const response = await apiService.post(
        'auth/forgot-password',
        { username },
        baseUrl
      );

      if (response.success && response.data?.is_success) {
        setMessage(response.data.message || 'If the username or email exists, a password reset link has been sent to the associated email address.');
      } else {
        setError(response.data?.message || 'Failed to process your request. Please try again.');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="login-container">
      <div className="login-main-content">
        {/* Left side - Image section */}
        <div className="login-image-section">
          <div className="image-container">
            <img
              src={maskGroupImage}
              alt="Chaithanya Eye Hospital"
              className="login-image"
            />
          </div>
        </div>

        {/* Right side - Forgot Password form section */}
        <div className="login-form-section">
          <div className="login-form-container">
            <div className="login-header">
              <h1>Forgot Password</h1>
              <p>Please enter your username below and click the Submit button. The system will send password reset instructions to your email address if a matching email address is found.</p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="login-form"
            >
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {message && (
                <div className="success-message">
                  {message}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter Username"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="forgot-password-container">
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={handleBackToLogin}
                  disabled={isLoading}
                >
                  Go back to Login
                </button>
              </div>

              <button
                type="submit"
                className="login-button"
                disabled={isLoading || !username.trim()}
              >
                {isLoading ? 'Submitting...' : 'Submit'}
              </button>
            </form>

            <div className="login-footer">
              <p className="copyright-text">
                © 2024 Healthcare. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
